/**
 * AI Chat Store
 * 使用 Zustand + localStorage 持久化
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { PersistStorage } from 'zustand/middleware';
import type { AIChatState, ContextType, Message, APIMessage, APIQuotaInfo, ChatMessageResponseData } from '@/types/aiChat';
import { sendMessageToAI, sendMessageToAIStream, getChatHistory, getQuota, clearChatHistory, STREAM_NOT_SUPPORTED_ERROR } from '@/apis/aiChat';
import { useUserStore } from '@/store/useUserStore';

const hashString = (input: string): number => {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) || 1;
};

// 字符上限前端固定为 500（不使用后端返回值）
const FIXED_CHARACTER_LIMIT = 500;

let activeStreamController: AbortController | null = null;

const abortActiveStream = () => {
  if (activeStreamController) {
    activeStreamController.abort();
    activeStreamController = null;
  }
};

// 转换 API 消息格式到内部格式
const convertAPIMessage = (apiMessage: APIMessage): Message => ({
  id: apiMessage.message_id ?? apiMessage.id ?? hashString(`${apiMessage.role}:${apiMessage.content}:${apiMessage.created_at ?? apiMessage.timestamp ?? ''}`),
  role: apiMessage.role,
  content: apiMessage.content,
  timestamp: apiMessage.created_at ?? apiMessage.timestamp ?? new Date().toISOString(),
});

// 转换 API 配额信息到内部格式
const convertAPIQuota = (apiQuota: APIQuotaInfo): AIChatState['quota'] => ({
  used: apiQuota.quota_used,
  limit: apiQuota.quota_limit,
  remaining: apiQuota.remaining,
  is_limit_reached: apiQuota.is_limit_reached ?? apiQuota.remaining === 0,
  character_limit: FIXED_CHARACTER_LIMIT,
});

const extractQuotaInfo = (payload: (Partial<APIQuotaInfo> & { quota?: APIQuotaInfo }) | null | undefined): APIQuotaInfo | null => {
  if (!payload) return null;
  if (payload.quota) return payload.quota;
  const { quota_used, quota_limit, remaining, character_limit, is_limit_reached } = payload;
  if (quota_used === undefined || quota_limit === undefined || remaining === undefined) return null;
  return { quota_used, quota_limit, remaining, character_limit, is_limit_reached };
};

const sortMessages = (messages: Message[]): Message[] => {
  return [...messages].sort((left, right) => {
    const leftTimestamp = Date.parse(left.timestamp);
    const rightTimestamp = Date.parse(right.timestamp);
    const leftTime = Number.isNaN(leftTimestamp) ? Number.POSITIVE_INFINITY : leftTimestamp;
    const rightTime = Number.isNaN(rightTimestamp) ? Number.POSITIVE_INFINITY : rightTimestamp;
    if (leftTime !== rightTime) return leftTime - rightTime;
    const leftId = typeof left.id === 'number' ? left.id : 0;
    const rightId = typeof right.id === 'number' ? right.id : 0;
    return leftId - rightId;
  });
};

const isSameContext = (left: AIChatState['currentContext'], right: AIChatState['currentContext']): boolean => {
  if (!left || !right) return false;
  return left.type === right.type && left.id === right.id;
};

const pickLatestMessage = (left: Message, right: Message): Message => {
  if (left.content.length !== right.content.length) {
    return left.content.length > right.content.length ? left : right;
  }
  const leftTimestamp = Date.parse(left.timestamp);
  const rightTimestamp = Date.parse(right.timestamp);
  const leftTime = Number.isNaN(leftTimestamp) ? Number.POSITIVE_INFINITY : leftTimestamp;
  const rightTime = Number.isNaN(rightTimestamp) ? Number.POSITIVE_INFINITY : rightTimestamp;
  return leftTime >= rightTime ? left : right;
};

const dedupeMessages = (messages: Message[]): Message[] => {
  const indexById = new Map<number, number>();
  const result: Message[] = [];
  messages.forEach((message) => {
    const existingIndex = indexById.get(message.id);
    if (existingIndex === undefined) {
      indexById.set(message.id, result.length);
      result.push(message);
      return;
    }
    const existing = result[existingIndex];
    if (!existing) {
      result[existingIndex] = message;
      return;
    }
    result[existingIndex] = pickLatestMessage(existing, message);
  });
  return sortMessages(result);
};

const mergeMessages = (existing: Message[], incoming: Message[]): Message[] => {
  const map = new Map<number, Message>();
  incoming.forEach((message) => {
    map.set(message.id, message);
  });
  existing.forEach((message) => {
    const prev = map.get(message.id);
    map.set(message.id, prev ? pickLatestMessage(message, prev) : message);
  });
  return sortMessages(Array.from(map.values()));
};

const getAIMessageFromResponse = (payload: ChatMessageResponseData): APIMessage | null => {
  return payload.ai_message || payload.message || null;
};

interface AIChatActions {
  // 设置当前上下文
  setCurrentContext: (type: ContextType, id: number) => void;
  // 打开/关闭对话框
  setModalOpen: (open: boolean) => void;
  // 关闭 Welcome Popup
  dismissWelcomePopup: (contextKey: string) => void;
  // 检查是否已关闭 Welcome Popup
  isWelcomePopupDismissed: (contextKey: string) => boolean;
  // 发送消息
  sendMessage: (message: string) => Promise<void>;
  // 加载聊天历史
  loadHistory: () => Promise<void>;
  // 加载配额信息
  loadQuota: () => Promise<void>;
  // 清空聊天记录
  clearMessages: () => Promise<void>;
  // 获取当前上下文的消息
  getCurrentMessages: () => Message[];
  // 终止当前流式请求
  abortStream: () => void;
}

type AIChatStore = AIChatState & AIChatActions;
type AIChatPersistedState = Pick<AIChatStore, 'messagesByContext' | 'welcomePopupDismissed'>;

// 生成上下文 key
const getContextKey = (type: ContextType, id: number): string => {
  return `${type}-${id}`;
};

const getUserStorageKey = (name: string): string => {
  const userId = useUserStore.getState().user?.id;
  return `${name}:${userId ?? 'guest'}`;
};

const baseStorage = createJSONStorage<AIChatPersistedState>(() => localStorage);
const userScopedStorage: PersistStorage<AIChatPersistedState> = {
  getItem: (name: string) => (baseStorage ? baseStorage.getItem(getUserStorageKey(name)) : null),
  setItem: (name: string, value) => {
    if (!baseStorage) return;
    baseStorage.setItem(getUserStorageKey(name), value);
  },
  removeItem: (name: string) => {
    if (!baseStorage) return;
    baseStorage.removeItem(getUserStorageKey(name));
  },
};

export const useAIChatStore = create<AIChatStore>()(
  persist<AIChatStore, [], [], AIChatPersistedState>(
    (set, get) => ({
      // 初始状态
      messagesByContext: {},
      welcomePopupDismissed: {},
      isModalOpen: false,
      currentContext: null,
      quota: null,
      isLoading: false,

      // 设置当前上下文
      setCurrentContext: (type: ContextType, id: number) => {
        set({ currentContext: { type, id } });
      },

      // 打开/关闭对话框
      setModalOpen: (open: boolean) => {
        set({ isModalOpen: open });
      },

      // 关闭 Welcome Popup
      dismissWelcomePopup: (contextKey: string) => {
        set((state) => ({
          welcomePopupDismissed: {
            ...state.welcomePopupDismissed,
            [contextKey]: true,
          },
        }));
      },

      // 检查是否已关闭 Welcome Popup
      isWelcomePopupDismissed: (contextKey: string) => {
        return get().welcomePopupDismissed[contextKey] || false;
      },

      // 发送消息
      sendMessage: async (message: string) => {
        const { currentContext } = get();
        if (!currentContext) return;

        const contextKey = getContextKey(currentContext.type, currentContext.id);
        const trimmedMessage = message.trim();
        if (!trimmedMessage) return;

        // 先添加用户消息到列表
        const localUserMessageId = Date.now();
        const userMessage: Message = {
          id: localUserMessageId,
          role: 'user',
          content: trimmedMessage,
          timestamp: new Date().toISOString(),
        };

        set((state) => ({
          messagesByContext: {
            ...state.messagesByContext,
            [contextKey]: [...(state.messagesByContext[contextKey] || []), userMessage],
          },
        }));

        abortActiveStream();
        const streamController = new AbortController();
        const assistantTempId = -Date.now();

        try {
          activeStreamController = streamController;

          set({ isLoading: true });

          const appendAssistantToken = (token: string) => {
            if (!token) return;
            set((state) => {
              const currentMessages = state.messagesByContext[contextKey] || [];
              const existingIndex = currentMessages.findIndex((item) => item.id === assistantTempId);
              if (existingIndex === -1) {
                return {
                  messagesByContext: {
                    ...state.messagesByContext,
                    [contextKey]: [
                      ...currentMessages,
                      {
                        id: assistantTempId,
                        role: 'assistant',
                        content: token,
                        timestamp: new Date().toISOString(),
                      },
                    ],
                  },
                };
              }
              const nextMessages = [...currentMessages];
              const targetMessage = nextMessages[existingIndex];
              if (!targetMessage) {
                return state;
              }
              nextMessages[existingIndex] = {
                ...targetMessage,
                content: `${targetMessage.content}${token}`,
              };
              return {
                messagesByContext: {
                  ...state.messagesByContext,
                  [contextKey]: nextMessages,
                },
              };
            });
          };

          const finalizeAssistantMessage = (assistantMessageId: number) => {
            set((state) => {
              const currentMessages = state.messagesByContext[contextKey] || [];
              const existingIndex = currentMessages.findIndex((item) => item.id === assistantTempId);
              if (existingIndex === -1) return state;
              const nextMessages = [...currentMessages];
              const targetMessage = nextMessages[existingIndex];
              if (!targetMessage) {
                return state;
              }
              nextMessages[existingIndex] = {
                ...targetMessage,
                id: assistantMessageId,
              };
              return {
                messagesByContext: {
                  ...state.messagesByContext,
                  [contextKey]: dedupeMessages(nextMessages),
                },
              };
            });
          };

          // 调用流式 API 发送消息
          await sendMessageToAIStream(
            currentContext.type,
            currentContext.id,
            trimmedMessage,
            {
              onStart: (event) => {
                if (!event.user_message_id) return;
                set((state) => {
                  const currentMessages = state.messagesByContext[contextKey] || [];
                  const nextMessages = currentMessages.map((item) => (item.id === localUserMessageId ? { ...item, id: event.user_message_id } : item));
                  return {
                    messagesByContext: {
                      ...state.messagesByContext,
                      [contextKey]: dedupeMessages(nextMessages),
                    },
                  };
                });
              },
              onToken: appendAssistantToken,
              onEnd: (event) => {
                finalizeAssistantMessage(event.assistant_message_id);
                const quotaInfo = extractQuotaInfo({
                  quota_used: event.quota_used,
                  quota_limit: event.quota_limit,
                  remaining: event.remaining,
                });
                if (quotaInfo) {
                  set({ quota: convertAPIQuota(quotaInfo) });
                }
              },
              onError: (errorMessage) => {
                console.error('AI Chat stream error:', errorMessage);
              },
            },
            streamController.signal
          );
        } catch (error) {
          if (error instanceof Error && error.message === STREAM_NOT_SUPPORTED_ERROR) {
            const response = await sendMessageToAI(currentContext.type, currentContext.id, trimmedMessage);
            const aiMessage = getAIMessageFromResponse(response);
            const quotaInfo = extractQuotaInfo(response);

            set((state) => {
              const currentMessages = state.messagesByContext[contextKey] || [];
              const nextMessages = aiMessage ? [...currentMessages, convertAPIMessage(aiMessage)] : currentMessages;

              return {
                messagesByContext: {
                  ...state.messagesByContext,
                  [contextKey]: nextMessages,
                },
                quota: quotaInfo ? convertAPIQuota(quotaInfo) : state.quota,
              };
            });

            if (!aiMessage) {
              console.warn('AI Chat response missing ai_message/message:', response);
            }
            return;
          }

          if (error instanceof Error && error.name === 'AbortError') {
            return;
          }

          console.error('Failed to send message:', error);
          throw error;
        } finally {
          if (activeStreamController === streamController) {
            activeStreamController = null;
            set({ isLoading: false });
          }
        }
      },

      // 加载聊天历史
      loadHistory: async () => {
        const { currentContext } = get();
        if (!currentContext) return;

        const contextSnapshot = currentContext;
        const contextKey = getContextKey(currentContext.type, currentContext.id);

        try {
          const response = await getChatHistory(currentContext.type, currentContext.id);
          if (!isSameContext(get().currentContext, contextSnapshot)) return;
          const messages = sortMessages((response.messages || []).map(convertAPIMessage));
          const quotaInfo = extractQuotaInfo(response);

          set((state) => ({
            messagesByContext: {
              ...state.messagesByContext,
              [contextKey]: mergeMessages(state.messagesByContext[contextKey] || [], messages),
            },
            quota: quotaInfo ? convertAPIQuota(quotaInfo) : state.quota,
          }));
        } catch (error) {
          console.error('Failed to load history:', error);
        }
      },

      // 加载配额信息
      loadQuota: async () => {
        const { currentContext } = get();
        if (!currentContext) return;

        try {
          const response = await getQuota(currentContext.type, currentContext.id);
          if (!isSameContext(get().currentContext, currentContext)) return;

          set({ quota: convertAPIQuota(response) });
        } catch (error) {
          console.error('Failed to load quota:', error);
        }
      },

      // 清空聊天记录
      clearMessages: async () => {
        const { currentContext } = get();
        if (!currentContext) return;

        const contextKey = getContextKey(currentContext.type, currentContext.id);

        try {
          abortActiveStream();
          set({ isLoading: false });

          await clearChatHistory(currentContext.type, currentContext.id);

          set((state) => ({
            messagesByContext: {
              ...state.messagesByContext,
              [contextKey]: [],
            },
          }));
        } catch (error) {
          console.error('Failed to clear messages:', error);
          throw error;
        }
      },

      // 获取当前上下文的消息
      getCurrentMessages: () => {
        const { currentContext, messagesByContext } = get();
        if (!currentContext) return [];

        const contextKey = getContextKey(currentContext.type, currentContext.id);
        return messagesByContext[contextKey] || [];
      },

      // 终止当前流式请求
      abortStream: () => {
        abortActiveStream();
        set({ isLoading: false });
      },
    }),
    {
      name: 'ai-chat-storage',
      storage: userScopedStorage,
      // 只持久化必要的数据
      partialize: (state): AIChatPersistedState => ({
        messagesByContext: state.messagesByContext,
        welcomePopupDismissed: state.welcomePopupDismissed,
      }),
    }
  )
);

// 切换账号时清空内存态，避免旧用户消息被写入新用户桶
useUserStore.subscribe((state, prevState) => {
  const nextUserId = state.user?.id ?? null;
  const prevUserId = prevState.user?.id ?? null;
  if (nextUserId === prevUserId) return;

  abortActiveStream();
  useAIChatStore.setState({
    messagesByContext: {},
    welcomePopupDismissed: {},
    quota: null,
    currentContext: null,
    isModalOpen: false,
    isLoading: false,
  });
});
